import React, {
  CSSProperties,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  ColumnDef,
  ExpandedState,
  PaginationState,
  Row,
  RowSelectionState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import {
  ColumnDescription,
  ExpandRowProps,
  SortOrder,
  TableChangeHandler
} from './types';
import classNames from 'classnames/bind';
import _ from 'lodash';
import { CSSTransition } from 'react-transition-group';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons';

import styles from './stickableBootstrapTable.module.scss';
import { TablePlaceholder } from 'components/TablePlaceholder/TablePlaceholder';
import i18n from 'i18n';
import { SetRefContext } from 'contexts/SetRefContext';
import TableRowToolBar from 'containers/TableRowToolBar/TableRowToolBar';
import {
  ListOperationArea,
  ListOperationAreaWithSearchBar
} from 'components/ListOperationArea/ListOperationArea';
import { ListEditBar } from 'components/ListEditBar/ListEditBar';
import {
  selectableColumnFormatter,
  selectableHeaderFormatter
} from 'components/TableColumn/TableColumn';
import RefContext from 'contexts/refContext';

interface BatchOperation {
  text: string;
  enable: boolean;
  hint: string;
  onClick: () => void;
}

export interface StickableTanstackTableProps<T = any> {
  customPadding?: boolean;
  hover?: boolean;
  hidePagination?: boolean;
  className?: string;
  scrollByElementControl?: boolean;
  data?: T[];
  noDataIndication?: string | (() => JSX.Element | string);
  stickHeader?: boolean;
  stickFooter?: boolean;
  stickFirstColumn?: boolean;
  stickLastColumn?: boolean;
  columns: ColumnDescription[];
  summaryData?: Partial<{ [K in keyof T]: string | number }>;
  keyField: string;
  remote?: boolean;
  defaultSorted?: [{ dataField: any; order: SortOrder }];
  expandRow?: ExpandRowProps<T, number>;
  selectFieldName?: string;
  renderRowBtns?: (row: T) => JSX.Element[];
  onTableChange?: TableChangeHandler<T>;
  rowClasses?: (row: any, rowIndex: number) => string;
  selectedRows?: (string | number)[];
  onSelect?: (id: string | number, select: boolean) => void;
  onSelectPage?: (checked: boolean) => void;
  clearSelectedRows?: () => void;
  defaultSearchString?: string;
  searchbarPlaceholder?: string;
  onHandleSearch?: (searchString: string) => void;
  renderListOperations?: () => JSX.Element[];
  batchOperaionConfig?: {
    title?: string;
    operation: BatchOperation | BatchOperation[];
  }[];
  pagination?: { page?: number; sizePerPage: number; totalSize?: number };
}

const cx = classNames.bind(styles);

const EXPAND_EXIT_MS = 400;

interface ExpandRowAnimProps {
  open: boolean;
  colSpan: number;
  children: React.ReactNode;
}

function ExpandRow({ open, colSpan, children }: ExpandRowAnimProps) {
  const [mounted, setMounted] = useState(open);
  const nodeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setMounted(true);
      return;
    }
    if (!mounted) return;
    const t = setTimeout(() => setMounted(false), EXPAND_EXIT_MS);
    return () => clearTimeout(t);
  }, [open, mounted]);

  if (!mounted) return null;

  return (
    <tr>
      <td className='reset-expansion-style' colSpan={colSpan}>
        <CSSTransition
          in={open}
          nodeRef={nodeRef}
          timeout={{ appear: 3000, enter: 3000, exit: EXPAND_EXIT_MS }}
          classNames='row-expand-slide'
          appear
        >
          <div ref={nodeRef}>{children}</div>
        </CSSTransition>
      </td>
    </tr>
  );
}

function defaultCompare(a: any, b: any, order: SortOrder) {
  if (order === 'desc') return a < b ? 1 : -1;
  return a > b ? 1 : -1;
}

// TanStack renders whatever `cell` / `header` holds through `flexRender`, which
// calls `createElement` on any function it is given. A renderer created inside
// convertColumns is therefore a brand new component type on every render, and
// React answers a changed type by unmounting the old subtree and mounting a new
// one — every cell loses its DOM node, its focus and any value the browser was
// keeping for it. These two components are created once, so a re-render updates
// the cells instead of replacing them.
//
// The per-render column description still reaches them through meta.rawColumn,
// so formatters and formatExtraData stay as fresh as they were before.
function rawColumnOf(ctx: any): ColumnDescription | undefined {
  return ctx.column.columnDef.meta?.rawColumn;
}

function FormatterCell(ctx: any) {
  const col = rawColumnOf(ctx);
  const value = ctx.getValue();
  if (col?.formatter) {
    return col.formatter(
      value,
      ctx.row.original,
      ctx.row.index,
      col.formatExtraData
    );
  }
  return value;
}

function FormatterHeader(ctx: any) {
  const col = rawColumnOf(ctx);
  const column = ctx.column;
  const sortDir = column.getIsSorted();
  // Only render the sort indicator span when the column is sortable.
  // The project-wide `th > span { width: 20px }` rule would otherwise
  // reserve 20px for a non-sortable column's empty span.
  const sortElement = column.getCanSort() ? (
    <span className={styles.sortIcon}>
      {sortDir === false ? (
        <>
          <FontAwesomeIcon icon={faCaretDown} className={styles.sortCaret} />
          <FontAwesomeIcon icon={faCaretUp} className={styles.sortCaret} />
        </>
      ) : sortDir === 'asc' ? (
        <FontAwesomeIcon icon={faCaretUp} className={styles.sortCaret} />
      ) : (
        <FontAwesomeIcon icon={faCaretDown} className={styles.sortCaret} />
      )}
    </span>
  ) : (
    <></>
  );

  if (col?.headerFormatter) {
    return (col.headerFormatter as any)(col, 0, { sortElement });
  }
  return (
    <>
      {col?.text}
      {sortElement}
    </>
  );
}

function convertColumns<T extends object>(
  columns: ColumnDescription[]
): ColumnDef<T, any>[] {
  return columns.map<ColumnDef<T, any>>((col) => {
    const userSortFunc = col.sortFunc;

    const sortingFn = col.sort
      ? (rowA: Row<T>, rowB: Row<T>, columnId: string) => {
          const a = (rowA.original as any)[columnId];
          const b = (rowB.original as any)[columnId];
          const order: SortOrder = 'asc';
          return userSortFunc
            ? (userSortFunc as any)(
                a,
                b,
                order,
                columnId,
                rowA.original,
                rowB.original
              )
            : defaultCompare(a, b, order);
        }
      : undefined;

    return {
      id: col.dataField,
      accessorKey: col.dataField,
      enableSorting: !!col.sort,
      sortingFn,
      meta: {
        formatExtraData: col.formatExtraData,
        classes: col.classes,
        headerClasses: col.headerClasses,
        style: col.style,
        headerStyle: col.headerStyle,
        text: col.text,
        rawColumn: col
      },
      header: FormatterHeader,
      cell: FormatterCell
    };
  });
}

// Returns a flat { [col.dataField]: value } map for the summary row.
// Only columns that actually appear in `summaryData` get a value — other
// columns render an empty cell.
function buildSummaryRow<T>(
  columns: ColumnDescription[],
  summaryData: Partial<{ [K in keyof T]: string | number }>
): Record<string, string | number | undefined> {
  const result: Record<string, string | number | undefined> = {};
  for (const col of columns) {
    const v = (summaryData as any)[col.dataField];
    if (v !== undefined) result[col.dataField] = v;
  }
  return result;
}

export function StickableTanstackTable<T extends object = any>(
  props: StickableTanstackTableProps<T>
) {
  const {
    customPadding = true,
    hover,
    hidePagination = false,
    className,
    scrollByElementControl = true,
    data,
    noDataIndication,
    stickHeader,
    stickFooter,
    stickFirstColumn = true,
    stickLastColumn,
    columns,
    summaryData,
    keyField,
    remote,
    defaultSorted,
    expandRow,
    selectFieldName,
    renderRowBtns,
    onTableChange,
    rowClasses,
    selectedRows,
    onSelect,
    onSelectPage,
    clearSelectedRows,
    defaultSearchString,
    searchbarPlaceholder,
    onHandleSearch,
    renderListOperations,
    batchOperaionConfig,
    pagination = { sizePerPage: 10 }
  } = props;

  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const [dividerVisible, setDividerVisible] = useState(false);

  const debouncedSearch = useMemo(
    () =>
      onHandleSearch
        ? _.debounce(onHandleSearch, remote ? 1000 : 0)
        : undefined,
    [onHandleSearch, remote]
  );

  const selectable = !!(onSelect && selectedRows);
  const searchable = !!debouncedSearch;

  const handleScroll = useCallback(() => {
    const el = tableContainerRef.current;
    if (!el) return;
    const vertical = stickHeader || stickFooter;
    const show = vertical ? el.scrollTop > 0 : el.scrollLeft > 0;
    setDividerVisible((prev) => (prev === show ? prev : show));
  }, [stickHeader, stickFooter]);

  useEffect(() => {
    const handler = () => setDividerVisible((v) => v);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // ----- Selection column formatter wrap (mirrors original behavior) -----
  const wrappedColumns = useMemo(() => {
    const result: ColumnDescription[] = columns.map((c) => ({ ...c }));

    const first = result[0];
    if (first && selectable) {
      const allData = _.defaultTo(data, []);
      let allSelected = false;
      let currentPageSelected = false;

      const page = pagination?.page;
      const sizePerPage = pagination?.sizePerPage ?? 10;

      if (page) {
        const offset = (page - 1) * sizePerPage;
        const end = offset + sizePerPage;
        let dataOfCurrentPage: T[];
        if (remote) {
          dataOfCurrentPage = allData;
          allSelected =
            selectedRows.length ===
              (pagination?.totalSize || dataOfCurrentPage.length) &&
            selectedRows.length > 0;
        } else {
          dataOfCurrentPage = allData.slice(offset, end);
          allSelected =
            selectedRows.length === allData.length && selectedRows.length > 0;
        }
        const selectKey = selectFieldName || keyField;
        const selectedInPage = dataOfCurrentPage.filter((d) =>
          selectedRows.includes((d as any)[selectKey])
        );
        currentPageSelected =
          dataOfCurrentPage.length === selectedInPage.length &&
          selectedInPage.length > 0;
      } else {
        allSelected = selectedRows.length === allData.length;
        currentPageSelected = allSelected;
      }

      const originalFormatter = first.formatter;
      const extraData = {
        allSelected,
        currentPageSelected,
        selectFieldName,
        selectedRows,
        onSelect,
        onSelectPage,
        columnFormatter: originalFormatter
          ? originalFormatter
          : (data: any) => data
      };
      first.formatExtraData = first.formatExtraData
        ? { ...first.formatExtraData, ...extraData }
        : extraData;
      first.formatter = selectableColumnFormatter as any;
      first.headerFormatter = selectableHeaderFormatter as any;
    }

    if (renderRowBtns) {
      result.push({
        dataField: 'editBtns',
        text: '',
        sort: false,
        style: () => ({ width: 0 }) as CSSProperties,
        headerStyle: () => ({ width: 0 }) as CSSProperties,
        formatter: (_1, row) => (
          <TableRowToolBar className={styles.editBtns}>
            {renderRowBtns(row as T)}
          </TableRowToolBar>
        )
      } as ColumnDescription);
    }
    return result;
  }, [
    columns,
    selectable,
    data,
    pagination,
    remote,
    selectedRows,
    selectFieldName,
    keyField,
    onSelect,
    onSelectPage,
    renderRowBtns
  ]);

  const tanstackColumns = useMemo(
    () => convertColumns<T>(wrappedColumns),
    [wrappedColumns]
  );

  // ----- Summary row (separated from tableData to avoid recomputing on column changes) -----
  const summaryRow = useMemo(() => {
    if (!summaryData) return undefined;
    return buildSummaryRow<T>(wrappedColumns, summaryData);
  }, [summaryData, wrappedColumns]);

  // Summary row is rendered separately at the top of the tbody (see render),
  // so it doesn't participate in sorting/pagination.
  const tableData = useMemo(() => _.defaultTo(data, []), [data]);

  // ----- TanStack state -----
  // defaultSorted is treated as the INITIAL sort (consumed once on mount).
  // After that, user clicks drive sorting. Resetting on every prop change
  // would also reset user's clicks because most callers pass an inline literal.
  const [sorting, setSorting] = useState<SortingState>(() => {
    if (!defaultSorted) return [];
    // Drop entries whose dataField doesn't match a real column — the OLD
    // library silently ignored them, TanStack logs a warning.
    const validDataFields = new Set(columns.map((c) => c.dataField));
    return defaultSorted
      .filter((s) => validDataFields.has(s.dataField))
      .map((s) => ({
        id: s.dataField,
        desc: s.order === 'desc'
      }));
  });

  const [paginationState, setPaginationStateRaw] = useState<PaginationState>(
    () => ({
      pageIndex: (pagination?.page ?? 1) - 1,
      pageSize: pagination?.sizePerPage ?? 10
    })
  );
  const setPaginationState = useCallback((u: any) => {
    setPaginationStateRaw((prev) => {
      const next = typeof u === 'function' ? u(prev) : u;
      // Bail out when values are identical — TanStack frequently calls
      // onPaginationChange with a new object that has the same values.
      if (
        next &&
        prev &&
        prev.pageIndex === next.pageIndex &&
        prev.pageSize === next.pageSize
      ) {
        return prev;
      }
      return next;
    });
  }, []);
  // Sync only when actual numeric values change (primitives, no ref churn).
  useEffect(() => {
    setPaginationState((prev) => {
      const next = {
        pageIndex: (pagination?.page ?? 1) - 1,
        pageSize: pagination?.sizePerPage ?? 10
      };
      return prev.pageIndex === next.pageIndex &&
        prev.pageSize === next.pageSize
        ? prev
        : next;
    });
  }, [pagination?.page, pagination?.sizePerPage, setPaginationState]);

  const [expanded, setExpanded] = useState<ExpandedState>({});

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  // ----- Fire onTableChange (sort/pagination) compatibly with the old API -----
  const prevSortRef = useRef<SortingState>(sorting);
  const prevPageRef = useRef<PaginationState>(paginationState);

  useEffect(() => {
    if (!onTableChange) return;
    const sortChanged = !_.isEqual(prevSortRef.current, sorting);
    const pageChanged = !_.isEqual(prevPageRef.current, paginationState);
    if (sortChanged) {
      const next = sorting[0];
      onTableChange(
        'sort' as any,
        {
          sortField: next?.id,
          sortOrder: next ? (next.desc ? 'desc' : 'asc') : undefined,
          page: paginationState.pageIndex + 1,
          sizePerPage: paginationState.pageSize
        } as any
      );
    } else if (pageChanged) {
      onTableChange(
        'pagination' as any,
        {
          sortField: sorting[0]?.id,
          sortOrder: sorting[0]
            ? sorting[0].desc
              ? 'desc'
              : 'asc'
            : undefined,
          page: paginationState.pageIndex + 1,
          sizePerPage: paginationState.pageSize
        } as any
      );
    }
    prevSortRef.current = sorting;
    prevPageRef.current = paginationState;
  }, [sorting, paginationState, onTableChange]);

  // ----- Build table -----
  const totalSize = _.defaultTo(pagination?.totalSize, tableData.length);

  // Memoize so TanStack sees stable references when nothing changed,
  // avoiding internal reducer churn on every parent re-render.
  const tableState = useMemo(
    () => ({
      sorting,
      pagination: paginationState,
      expanded,
      rowSelection
    }),
    [sorting, paginationState, expanded, rowSelection]
  );

  const getRowIdStable = useCallback(
    (row: T) => String((row as any)[keyField] ?? (row as any).id),
    [keyField]
  );
  const getRowCanExpandStable = useCallback(() => !!expandRow, [expandRow]);

  // Row-model getters are stable across renders so TanStack doesn't rebuild
  // its internal pipelines when nothing actually changed.
  const coreRowModelGetter = useMemo(() => getCoreRowModel(), []);
  const sortedRowModelGetter = useMemo(() => getSortedRowModel(), []);
  const paginatedRowModelGetter = useMemo(() => getPaginationRowModel(), []);
  const expandedRowModelGetter = useMemo(() => getExpandedRowModel(), []);

  const table = useReactTable<T>({
    data: tableData,
    columns: tanstackColumns,
    state: tableState,
    onSortingChange: setSorting,
    onPaginationChange: setPaginationState,
    onExpandedChange: setExpanded,
    onRowSelectionChange: setRowSelection,
    getRowId: getRowIdStable,
    getRowCanExpand: getRowCanExpandStable,
    manualPagination: !!remote,
    manualSorting: !!remote,
    // Disable auto-reset behaviors that ping setPaginationState/setExpanded
    // every render — we manage these explicitly and they cause re-render loops
    // on tables that don't paginate locally (hidePagination + summaryData).
    autoResetPageIndex: false,
    autoResetExpanded: false,
    pageCount: remote
      ? Math.max(1, Math.ceil(totalSize / paginationState.pageSize))
      : undefined,
    getCoreRowModel: coreRowModelGetter,
    getSortedRowModel: remote ? undefined : sortedRowModelGetter,
    getPaginationRowModel: hidePagination ? undefined : paginatedRowModelGetter,
    getExpandedRowModel: expandRow ? expandedRowModelGetter : undefined
  });

  const setRefCtx = useContext(SetRefContext);
  // The ElementScrollControl Provider passes `value={{ setRef: this.setRef }}`,
  // creating a new wrapper object every render. Only the inner method is stable —
  // depend on that, not the outer context object.
  const externalSetRef = setRefCtx?.setRef;
  const externalSetRefRef = useRef(externalSetRef);
  externalSetRefRef.current = externalSetRef;

  // Track the mounted node via state so the ResizeObserver effect below can
  // (re-)attach whenever the table div mounts. The container may be conditionally
  // rendered (placeholder vs table), so a useRef + once-only effect would miss
  // the actual mount.
  const [tableNode, setTableNode] = useState<HTMLDivElement | null>(null);

  // Notify "no scroll width to track" when placeholder is shown.
  useEffect(() => {
    const showPlaceholder = !!noDataIndication && (data?.length ?? 0) === 0;
    if (showPlaceholder && externalSetRef) externalSetRef(undefined);
  }, [data, noDataIndication, externalSetRef]);

  // Cleanup once on unmount only — do not fire on every re-render.
  useEffect(() => {
    return () => {
      externalSetRefRef.current && externalSetRefRef.current(undefined);
    };
  }, []);

  // Sync scrollWidth back to ElementScrollControl whenever the table resizes.
  // ResizeObserver re-fires setRef only when the container's box actually
  // changes, so hiddenSpaceWidth stays in sync with the real table width
  // without re-attaching the ref every render.
  useEffect(() => {
    const RO = (window as any).ResizeObserver as
      | typeof ResizeObserver
      | undefined;
    if (!tableNode || !externalSetRef || !RO) {
      return;
    }
    const observer = new RO(() => {
      externalSetRef(tableNode);
    });
    observer.observe(tableNode);
    // Also trigger an initial sync in case the container mounted before data
    // arrived and its scrollWidth has since grown.
    externalSetRef(tableNode);
    return () => observer.disconnect();
  }, [tableNode, externalSetRef]);

  // ----- Render helpers -----
  const renderTablePlaceholder = () => {
    if (!noDataIndication) return undefined;
    if (typeof noDataIndication === 'string') {
      return (
        <div className={styles.placeholder}>
          <TablePlaceholder placeholder={i18n.t(noDataIndication)} />
        </div>
      );
    }
    return noDataIndication();
  };

  const renderBatchOperations = () => {
    if (!selectedRows) return <div />;
    if (!batchOperaionConfig) return undefined;

    const handleNavEvent = (e: React.MouseEvent) => e && e.stopPropagation();
    const makeOperationClick = (onClick: () => void) => (e: any) => {
      e && e.stopPropagation();
      onClick();
    };
    const renderOperations = () =>
      batchOperaionConfig.map((config) => {
        const op = config.operation;
        if (Array.isArray(op)) {
          return (
            <ListEditBar.Dropdown
              key={config.title}
              title={config.title}
              id='creativeManagement'
              onClick={handleNavEvent}
            >
              {op.map((o) => (
                <ListEditBar.Dropdown.Item
                  key={o.text}
                  onClick={makeOperationClick(o.onClick)}
                  disabled={!o.enable}
                >
                  {o.text}
                </ListEditBar.Dropdown.Item>
              ))}
            </ListEditBar.Dropdown>
          );
        }
        const tipClass = cx(styles.tipContainer, { block: !op.enable });
        return (
          <div className={tipClass} key={op.text}>
            <div className={styles.tooltip}>{op.hint}</div>
            <ListEditBar.Item>
              <ListEditBar.Link
                onClick={makeOperationClick(op.onClick)}
                disabled={!op.enable}
              >
                {op.text}
              </ListEditBar.Link>
            </ListEditBar.Item>
          </div>
        );
      });

    return (
      <ListEditBar
        selectedItems={selectedRows}
        handleRemoveSelect={clearSelectedRows}
      >
        {renderOperations()}
      </ListEditBar>
    );
  };

  const handleOnSearch = (searchString: string) => {
    if (!debouncedSearch) return;
    if (searchString === '') {
      debouncedSearch.cancel();
      onHandleSearch && onHandleSearch(searchString);
      return;
    }
    debouncedSearch(searchString);
  };

  const showBatchOperations =
    selectable && (selectedRows?.length ?? 0) > 0 && !!batchOperaionConfig;
  const hasData = (data?.length ?? 0) > 0;
  const showPlaceholder = !!noDataIndication && !hasData;

  const containerClass = cx('stickableTableContainer', {
    paddingBottom: hasData && !hidePagination,
    withPlaceholder: showPlaceholder
  });
  const tableClass = cx('stickableTable', className, {
    stickHeader,
    stickFooter,
    stickFirstColumn: !stickHeader && stickFirstColumn,
    stickLastColumn: !stickHeader && (stickLastColumn || !!renderRowBtns),
    showDivider: dividerVisible,
    horizontal: stickHeader || stickFooter,
    scrollByElementControl
  });

  // ----- Row models we actually want to render -----
  const visibleRows = remote
    ? table.getRowModel().rows
    : hidePagination
      ? table.getSortedRowModel().rows
      : table.getRowModel().rows;

  // ----- Pagination UI (mimics react-bootstrap-table2-paginator DOM) -----
  const renderPagination = () => {
    if (hidePagination) return null;
    const totalItems = remote ? totalSize : tableData.length;
    const pageSize = pagination?.sizePerPage ?? 10;
    const pageCount = Math.max(1, Math.ceil(totalItems / pageSize));
    const current = paginationState.pageIndex + 1;
    const go = (next: number) => {
      const target = Math.min(Math.max(1, next), pageCount);
      setPaginationState((s) => ({ ...s, pageIndex: target - 1 }));
    };
    const goFirst = () => go(1);
    const goPrev = () => go(current - 1);
    const goNext = () => go(current + 1);
    const goLast = () => go(pageCount);
    const goToPage = (e: React.MouseEvent<HTMLButtonElement>) =>
      go(Number(e.currentTarget.dataset.page));

    // sliding window of at most WINDOW pages around current
    const WINDOW = 5;
    let start: number;
    let end: number;
    if (pageCount <= WINDOW) {
      start = 1;
      end = pageCount;
    } else {
      const half = Math.floor(WINDOW / 2);
      start = Math.max(1, current - half);
      end = start + WINDOW - 1;
      if (end > pageCount) {
        end = pageCount;
        start = end - WINDOW + 1;
      }
    }
    const pages = _.range(start, end + 1);

    const showFirst = current > 3;
    const showPrev = current > 1;
    const showNext = current < pageCount;
    const showLast = current < pageCount - 2;

    return (
      <div className='row react-bootstrap-table-pagination'>
        <div className='col-md-6 col-xs-6 col-sm-6 col-lg-6' />
        <div className='col-md-6 col-xs-6 col-sm-6 col-lg-6 react-bootstrap-table-pagination-list'>
          <ul className='pagination'>
            {showFirst && (
              <li className='page-item'>
                <button
                  className='page-link'
                  onClick={goFirst}
                  aria-label='First'
                >
                  &laquo;
                </button>
              </li>
            )}
            {showPrev && (
              <li className='page-item'>
                <button
                  className='page-link'
                  onClick={goPrev}
                  aria-label='Previous'
                >
                  &lsaquo;
                </button>
              </li>
            )}
            {pages.map((p) => (
              <li
                key={p}
                className={`page-item ${p === current ? 'active' : ''}`}
              >
                <button className='page-link' data-page={p} onClick={goToPage}>
                  {p}
                </button>
              </li>
            ))}
            {showNext && (
              <li className='page-item'>
                <button
                  className='page-link'
                  onClick={goNext}
                  aria-label='Next'
                >
                  &rsaquo;
                </button>
              </li>
            )}
            {showLast && (
              <li className='page-item'>
                <button
                  className='page-link'
                  onClick={goLast}
                  aria-label='Last'
                >
                  &raquo;
                </button>
              </li>
            )}
          </ul>
        </div>
      </div>
    );
  };

  // Preserve the old library's contract: when a summary row exists, real-data
  // row indices are offset by 1 so rowIndex === 0 still maps to the summary.
  const dataRowIndexOffset = summaryRow ? 1 : 0;
  const renderRowClasses = (row: Row<T>) =>
    rowClasses
      ? rowClasses(row.original, row.index + dataRowIndexOffset)
      : undefined;

  const setExternalRef = setRefCtx?.setRef;
  // Track the mounted node via state so the ResizeObserver effect below can
  const tableScrollRef = useCallback(
    (node: HTMLDivElement | null) => {
      tableContainerRef.current = node;
      setTableNode(node);
      setExternalRef && setExternalRef(node);
    },
    [setExternalRef]
  );

  return (
    <>
      <div className={containerClass}>
        {showBatchOperations ? (
          renderBatchOperations()
        ) : searchable ? (
          <ListOperationAreaWithSearchBar
            defaultSearchString={defaultSearchString!}
            searchbarPlaceholder={_.defaultTo(searchbarPlaceholder, '')}
            onHandleSearch={handleOnSearch}
          >
            {renderListOperations ? renderListOperations() : <div />}
          </ListOperationAreaWithSearchBar>
        ) : (
          renderListOperations && (
            <ListOperationArea>{renderListOperations()}</ListOperationArea>
          )
        )}
        {showPlaceholder ? (
          renderTablePlaceholder()
        ) : (
          <div
            className={tableClass}
            onScroll={handleScroll}
            ref={tableScrollRef}
          >
            <RefContext.Provider value={{ ref: tableContainerRef }}>
              <div className='react-bootstrap-table'>
                <table
                  className={cx('table', 'table-bordered', {
                    'table-hover': hover
                  })}
                >
                  <thead>
                    {table.getHeaderGroups().map((hg) => (
                      <tr key={hg.id}>
                        {hg.headers.map((h, headerIdx) => {
                          const meta = h.column.columnDef.meta as any;
                          const userHeaderStyle =
                            (meta?.headerStyle && meta.headerStyle()) ||
                            undefined;
                          const defaultPad =
                            !customPadding && headerIdx === 0
                              ? { paddingLeft: selectable ? '14px' : '64px' }
                              : undefined;
                          const headerClasses =
                            (meta?.headerClasses && meta.headerClasses()) ||
                            undefined;
                          const toggleSorting = h.column.getCanSort()
                            ? h.column.getToggleSortingHandler()
                            : undefined;
                          // Don't trigger sorting when the click lands on an
                          // interactive control inside the header (e.g. the
                          // select-all checkbox/label in a selectable first
                          // column). Mirrors the row-click guard below.
                          const onClick = toggleSorting
                            ? (e: React.MouseEvent) => {
                                const target = e.target as HTMLElement;
                                if (
                                  target.closest(
                                    'input, button, a, label, select, textarea'
                                  )
                                ) {
                                  return;
                                }
                                toggleSorting(e);
                              }
                            : undefined;
                          return (
                            <th
                              key={h.id}
                              className={headerClasses}
                              style={{
                                cursor: h.column.getCanSort()
                                  ? 'pointer'
                                  : 'default',
                                ...defaultPad,
                                ...userHeaderStyle
                              }}
                              onClick={onClick}
                            >
                              {flexRender(
                                h.column.columnDef.header,
                                h.getContext()
                              )}
                            </th>
                          );
                        })}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {summaryRow && (
                      <tr
                        className={cx(
                          'summaryRow',
                          rowClasses ? rowClasses(summaryRow, 0) : undefined
                        )}
                      >
                        {wrappedColumns.map((col, idx) => {
                          const value = summaryRow[col.dataField];
                          const tdClasses =
                            typeof col.classes === 'function'
                              ? (col.classes as any)(value, summaryRow, 0, idx)
                              : col.classes;
                          const userStyle =
                            typeof col.style === 'function'
                              ? (col.style as any)(value, summaryRow, 0, idx)
                              : col.style;
                          const defaultPad =
                            !customPadding && idx === 0
                              ? {
                                  paddingLeft: selectable ? '14px' : '64px'
                                }
                              : undefined;
                          return (
                            <td
                              key={col.dataField}
                              className={tdClasses}
                              style={
                                defaultPad || userStyle
                                  ? { ...defaultPad, ...userStyle }
                                  : undefined
                              }
                            >
                              {value !== undefined ? <div>{value}</div> : null}
                            </td>
                          );
                        })}
                      </tr>
                    )}
                    {visibleRows.map((row) => {
                      const allowExpand =
                        expandRow &&
                        (expandRow.nonExpandable
                          ? !expandRow.nonExpandable.includes(
                              (row.original as any)[keyField]
                            )
                          : true);
                      const onRowClick = allowExpand
                        ? (e: React.MouseEvent) => {
                            const target = e.target as HTMLElement;
                            if (
                              target.closest(
                                'input, button, a, label, select, textarea'
                              )
                            ) {
                              return;
                            }
                            row.toggleExpanded();
                          }
                        : undefined;
                      return (
                        <React.Fragment key={row.id}>
                          <tr
                            className={renderRowClasses(row)}
                            onClick={onRowClick}
                            style={
                              onRowClick ? { cursor: 'pointer' } : undefined
                            }
                          >
                            {row.getVisibleCells().map((cell, cellIdx) => {
                              const meta = cell.column.columnDef.meta as any;
                              const userTdStyle =
                                typeof meta?.style === 'function'
                                  ? meta.style(
                                      (cell.row.original as any)[
                                        cell.column.id
                                      ],
                                      cell.row.original,
                                      cell.row.index,
                                      cell.column.getIndex()
                                    )
                                  : meta?.style || undefined;
                              const defaultPad =
                                !customPadding && cellIdx === 0
                                  ? {
                                      paddingLeft: selectable ? '14px' : '64px'
                                    }
                                  : undefined;
                              const tdClasses =
                                typeof meta?.classes === 'function'
                                  ? meta.classes(
                                      (cell.row.original as any)[
                                        cell.column.id
                                      ],
                                      cell.row.original,
                                      cell.row.index,
                                      cell.column.getIndex()
                                    )
                                  : meta?.classes || undefined;
                              return (
                                <td
                                  key={cell.id}
                                  className={tdClasses}
                                  style={
                                    defaultPad || userTdStyle
                                      ? { ...defaultPad, ...userTdStyle }
                                      : undefined
                                  }
                                >
                                  {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext()
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                          {expandRow && allowExpand && (
                            <ExpandRow
                              open={!!row.getIsExpanded()}
                              colSpan={row.getVisibleCells().length}
                            >
                              {(expandRow.renderer as any)(
                                row.original,
                                row.index
                              )}
                            </ExpandRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  {wrappedColumns.some((c) => c.footer !== undefined) && (
                    <tfoot>
                      <tr>
                        {wrappedColumns.map((col, idx) => {
                          const footerValue =
                            typeof col.footer === 'function'
                              ? col.footer(
                                  tableData.map((r: any) => r[col.dataField]),
                                  col,
                                  idx
                                )
                              : col.footer;
                          const footerClasses =
                            typeof col.footerClasses === 'function'
                              ? (col.footerClasses as any)(col, idx)
                              : col.footerClasses;
                          return (
                            <th key={col.dataField} className={footerClasses}>
                              {footerValue ?? null}
                            </th>
                          );
                        })}
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </RefContext.Provider>
          </div>
        )}
        {/* Render pagination as a sibling of the scrollable container so its
            Bootstrap `.row` negative margins don't bleed into scrollWidth. */}
        {!showPlaceholder && renderPagination()}
      </div>
    </>
  );
}
